import { useEffect, useMemo, useState } from 'react'
import debounce from 'lodash/debounce'
import { useQuery } from '@apollo/client'
import { Row, Col } from 'react-bootstrap'

import { AREAS, QueryAreasArgs, TDataAreas } from 'graphqlAPI'
import { IAreaModel } from 'models/area.model'
import Table from './table'
import styles from './Areas.module.scss'
import { sortTableEnum } from 'enum/sortTable.enum'
import { ApplyFiltersByType } from '../../types'
import { ChildFilterTypes } from 'enum'

interface AreasProps {
  area?: IAreaModel | null
  applyFiltersByType: ApplyFiltersByType
}

export const Areas = ({ area, applyFiltersByType }: AreasProps) => {
  const [text, setText] = useState('')
  const [perPage, setPerPage] = useState(10)
  const [page, setPage] = useState(1)
  const [sorts, setSorts] = useState<string[]>([])
  const areaId = area?.id

  const g = useMemo(() => {
    const { filters } = applyFiltersByType(ChildFilterTypes.Area)
    return filters || []
  }, [applyFiltersByType])

  const variablesBase = useMemo<QueryAreasArgs>(
    () => ({
      idOrParentId: areaId?.toString(),
      q:
        sorts.length || text.length || g.length
          ? {
              sorts: sorts,
              name_or_code_cont: text,
              g,
            }
          : undefined,
    }),
    [areaId, sorts, text, g]
  )

  const variables = useMemo<QueryAreasArgs>(
    () => ({
      ...variablesBase,
      perPage,
      page,
    }),
    [variablesBase, perPage, page]
  )

  useEffect(() => {
    setPage(1)
  }, [variablesBase, perPage])

  useEffect(() => {
    setPerPage(10)
  }, [variablesBase])

  const setTextDebounced = useMemo(
    () => debounce(setText, 1000, { maxWait: 2000 }),
    [setText]
  )

  const { data, loading } = useQuery<TDataAreas, QueryAreasArgs>(AREAS, {
    variables,
    nextFetchPolicy: 'cache-and-network',
    fetchPolicy: 'cache-and-network',
  })

  const { nodes = [], nodesCount } = useMemo(
    () => ({ ...data?.areas }),
    [data?.areas]
  )

  return (
    <div className={styles.message}>
      <Row>
        <Col xs={12}>
          <Table
            setPage={(newPage: number) => {
              setPage(newPage)
            }}
            setSearch={(newSearch: string) => {
              setTextDebounced(newSearch)
            }}
            setSort={({ field, order }) => {
              setSorts(order ? [`${field} ${sortTableEnum[order]}`] : [])
            }}
            setPerPage={(newPerPage: number) => {
              setPerPage(newPerPage)
            }}
            loading={loading}
            list={nodes}
            total={nodesCount}
            currentPage={page}
          />
        </Col>
      </Row>
    </div>
  )
}
